import React, { useState } from 'react';
import {
  List,
  ListItem,
  ListItemText,
  Button,
  Paper,
  Checkbox,
  ListItemButton,
} from '@mui/material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
interface ITransferListProps {
  defaultFields: string[];
  allFields: string[];
  onFieldsChange: (fields: string[]) => void;
}
const SortableItem = ({ id, value }: { id: string; value: string }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    cursor: 'grab',
  };
  return (
    <ListItem
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
    >
      <ListItemText primary={value} />
    </ListItem>
  );
};
const TransferList: React.FC<ITransferListProps> = ({
  defaultFields,
  allFields,
  onFieldsChange,
}) => {
  const [leftItems, setLeftItems] = useState<string[]>(
    allFields.filter((field) => !defaultFields.includes(field))
  );
  const [rightItems, setRightItems] = useState<string[]>(defaultFields);
  const [selectedLeft, setSelectedLeft] = useState<string[]>([]);
  const [selectedRight, setSelectedRight] = useState<string[]>([]);
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;
    const activeId = String(active.id);
    const overId = String(over.id);
    if (rightItems.includes(activeId) && rightItems.includes(overId)) {
      const oldIndex = rightItems.indexOf(activeId);
      const newIndex = rightItems.indexOf(overId);
      const newRightItems = arrayMove(rightItems, oldIndex, newIndex);
      setRightItems(newRightItems);
      onFieldsChange(newRightItems);
    }
  };
  const handleSelectLeft = (value: string) => {
    setSelectedLeft((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };
  const handleSelectRight = (value: string) => {
    setSelectedRight((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };
  const handleMoveRight = () => {
    setRightItems([...rightItems, ...selectedLeft]);
    setLeftItems(leftItems.filter((item) => !selectedLeft.includes(item)));
    setSelectedLeft([]);
    onFieldsChange([...rightItems, ...selectedLeft]);
  };
  const handleMoveLeft = () => {
    setLeftItems([...leftItems, ...selectedRight]);
    setRightItems(rightItems.filter((item) => !selectedRight.includes(item)));
    setSelectedRight([]);
    onFieldsChange(rightItems.filter((item) => !selectedRight.includes(item)));
  };
  const handleMoveAllRight = () => {
    setRightItems([...rightItems, ...leftItems]);
    setLeftItems([]);
    setSelectedLeft([]);
    onFieldsChange([...rightItems, ...leftItems]);
  };
  const handleMoveAllLeft = () => {
    setLeftItems([...leftItems, ...rightItems]);
    setRightItems([]);
    setSelectedRight([]);
    onFieldsChange([]);
  };
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '16px' }}>
      <div>
        <Paper sx={{ width: 250, height: 300, overflow: 'auto' }}>
          <List dense component="div" role="list">
            {leftItems.map((value) => (
              <ListItemButton key={value} onClick={() => handleSelectLeft(value)}>
                <Checkbox checked={selectedLeft.includes(value)} />
                <ListItemText primary={value} />
              </ListItemButton>
            ))}
          </List>
        </Paper>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Button
          variant="outlined"
          size="small"
          onClick={handleMoveRight}
          disabled={selectedLeft.length === 0}
          aria-label="move selected right"
        >
          &gt;
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleMoveAllRight}
          disabled={leftItems.length === 0}
          aria-label="move all right"
        >
          ≫
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleMoveAllLeft}
          disabled={rightItems.length === 0}
          aria-label="move all left"
        >
          ≪
        </Button>
        <Button
          variant="outlined"
          size="small"
          onClick={handleMoveLeft}
          disabled={selectedRight.length === 0}
          aria-label="move selected left"
        >
          &lt;
        </Button>
      </div>
      <div>
        <Paper sx={{ width: 250, height: 300, overflow: 'auto' }}>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext items={rightItems} strategy={verticalListSortingStrategy}>
              <List dense component="div" role="list">
                {rightItems.map((value) => (
                  <ListItemButton
                    key={value}
                    onClick={() => handleSelectRight(value)}
                  >
                    <Checkbox checked={selectedRight.includes(value)} />
                    <SortableItem id={value} value={value} />
                  </ListItemButton>
                ))}
              </List>
            </SortableContext>
          </DndContext>
        </Paper>
      </div>
    </div>
  );
};
export default TransferList;